/**
 * One article: what the ERP says, what we add, and what the customer sees.
 *
 * Three columns, as the design draws them, and the split is the point. An
 * operator on this screen is answering one of three questions — is the ERP
 * data right, is the record complete enough to publish, and what will a
 * customer actually see — and each column answers one.
 *
 * **The right column is the only place the reserved quantity appears**, and it
 * is labelled as not editable. It is derived from this portal's own unshipped
 * orders; an input there would let somebody put what customers see permanently
 * out of step with what they ordered.
 *
 * **An attribute's source is shown next to its value.** A derived value will
 * be replaced by the next parse and a typed one never will, and an operator
 * about to correct something needs to know which they are looking at before
 * they spend the effort.
 *
 * Nothing here authorises anything: `referential.article.write` decides
 * whether the inputs appear, `referential.article.publish` whether the publish
 * button does, and the server re-authorises both — ADR-0008.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "@tanstack/react-router";
import { useCan } from "@hardauto/core-access";
import type { ApiClient } from "@hardauto/core-api";
import { useAction, useResource } from "@hardauto/core-data";
import { Field } from "@hardauto/core-forms";
import { quantity } from "@hardauto/core-i18n";
import {
  Button,
  Card,
  DataTable,
  ErrorState,
  Input,
  LoadingState,
  Page,
  Select,
  StatusDot,
  useToast,
  type Column,
} from "@hardauto/core-ui";

import {
  ReferentialApi,
  type ArticleAttribute,
  type PublicationStatus,
  type StockDisclosure,
  type StockLevel,
} from "../api";
import { NAMESPACE } from "../i18n";
import { ArticleCoreCard } from "./ArticleCoreCard";
import { ArticlePerimeterCard } from "./ArticlePerimeterCard";
import { ArticlePricesCard } from "./ArticlePricesCard";
import { REFERENTIAL_PATHS } from "../paths";
import "./referential.css";

/** The attribute key the parser writes original-equipment references under. */
const OE_REFERENCE = "oe_reference";

const STATUS_TONE: Record<PublicationStatus, "success" | "neutral" | "danger"> = {
  published: "success",
  draft: "neutral",
  archived: "danger",
};

const DISCLOSURES: readonly StockDisclosure[] = ["bands", "exact", "hidden"];

export function ArticleScreen({ client, id }: { client: ApiClient; id: string }) {
  const { t, i18n } = useTranslation(NAMESPACE);
  const api = useMemo(() => new ReferentialApi(client), [client]);
  const toast = useToast();
  const action = useAction();

  const mayWrite = useCan("referential.article.write");
  const mayPublish = useCan("referential.article.publish");
  const mayWriteStock = useCan("referential.stock.write");
  const mayReadPrices = useCan("referential.pricing.read");
  const mayWritePrices = useCan("referential.pricing.write");

  const fetcher = useCallback(() => api.getArticle(id), [api, id]);
  const detail = useResource(fetcher);

  const [newKey, setNewKey] = useState("");
  const [newValue, setNewValue] = useState("");
  const [referenceBase, setReferenceBase] = useState("");
  const [codeFournisseur, setCodeFournisseur] = useState("");
  const [ipc, setIpc] = useState("");

  const article = detail.data?.article;
  const levels = detail.data?.stock ?? [];

  // Mirrors the server's values whenever a different article loads (or this
  // one reloads after a save) — a plain `useState` initialiser only runs
  // once, and the article arrives after the first render.
  useEffect(() => {
    setReferenceBase(article?.reference_base ?? "");
    setCodeFournisseur(article?.code_fournisseur ?? "");
    setIpc(article?.ipc ?? "");
  }, [article?.id, article?.reference_base, article?.code_fournisseur, article?.ipc]);

  /** Run a mutation, then re-read. The server decides the resulting state. */
  const mutate = useCallback(
    (key: string, task: () => Promise<unknown>) =>
      action.run(key, async () => {
        try {
          await task();
          // Re-read rather than patching local state: publishing can change
          // more than the status, and guessing here would put the screen and
          // the truth one release apart.
          detail.reload();
        } catch {
          toast.show(t("error.title"), { tone: "danger" });
        }
      }),
    [action, detail, toast, t],
  );

  if (detail.loading) return <LoadingState label={t("article.loading")} />;
  if (!article) {
    return (
      // `exactOptionalPropertyTypes` is on, so an absent prop and one set to
      // `undefined` are different types. Spread rather than a ternary.
      <ErrorState
        title={detail.failed ? t("error.title") : t("article.notFound")}
        {...(detail.failed ? { onRetry: detail.reload, retryLabel: t("error.retry") } : {})}
      />
    );
  }

  const equivalents = article.attributes.filter((entry) => entry.key === OE_REFERENCE);
  const characteristics = article.attributes.filter((entry) => entry.key !== OE_REFERENCE);

  const attributeColumns: Column<ArticleAttribute>[] = [
    { key: "key", header: t("article.attributes.key"), render: (row) => <code>{row.key}</code> },
    { key: "value", header: t("article.attributes.value"), render: (row) => row.value },
    {
      key: "source",
      header: t("article.attributes.source"),
      render: (row) => (
        <span title={t(`article.attributes.sourceHint.${row.source}`)}>
          {t(`article.attributes.sourceValue.${row.source}`)}
        </span>
      ),
    },
    {
      key: "actions",
      header: "",
      render: (row) =>
        mayWrite ? (
          <Button
            variant="ghost"
            size="sm"
            busy={action.isBusy(`attr:${row.key}`)}
            busyLabel={t("article.attributes.remove")}
            onClick={() =>
              void mutate(`attr:${row.key}`, () => api.removeAttribute(article.id, row.key))
            }
          >
            {t("article.attributes.remove")}
          </Button>
        ) : null,
    },
  ];

  return (
    <Page
      title={article.designation}
      back={<Link to={REFERENTIAL_PATHS.articles}>{t("article.back")}</Link>}
      eyebrow={
        <>
          {article.reference} ·{" "}
          {t(`article.origin.${article.origin === "external" ? "external" : "portal"}`)}
          {article.external_ref && article.external_ref !== article.reference
            ? ` · ${article.external_ref}`
            : ""}
        </>
      }
      actions={
        <>
          <StatusDot
            label={t(`article.active.${article.is_active ? "active" : "inactive"}`)}
            tone={article.is_active ? "success" : "danger"}
          />
          {mayWrite ? (
            <Select
              aria-label={t("article.active.label")}
              value={article.is_active ? "active" : "inactive"}
              disabled={action.isBusy("active")}
              onChange={(event) =>
                void mutate("active", () =>
                  api.updateArticle(article.id, {
                    is_active: event.target.value === "active",
                  }),
                )
              }
            >
              <option value="active">{t("article.active.active")}</option>
              <option value="inactive">{t("article.active.inactive")}</option>
            </Select>
          ) : null}
          <StatusDot
            label={t(`article.status.${article.status}`)}
            tone={STATUS_TONE[article.status]}
          />
          {mayPublish && article.status !== "archived" ? (
            <Button
              variant="primary"
              busy={action.isBusy("status")}
              busyLabel={t("article.publish")}
              onClick={() =>
                void mutate("status", () =>
                  api.setArticleStatus(
                    article.id,
                    article.status === "published" ? "draft" : "published",
                  ),
                )
              }
            >
              {article.status === "published" ? t("article.withdraw") : t("article.publish")}
            </Button>
          ) : null}
        </>
      }
    >
      <div className="hardauto-article">
        {/* --- What the ERP says ------------------------------------------ */}
        <div className="hardauto-article__stack">
          <ArticleCoreCard
            api={api}
            client={client}
            article={article}
            mayWrite={mayWrite}
            onSaved={detail.reload}
          />
        </div>

        {/* --- What we add ------------------------------------------------ */}
        <div className="hardauto-article__stack">
          <Card
            title={t("article.attributes.title")}
            compactTitle
            flush
            actions={
              mayWrite ? (
                <Button
                  size="sm"
                  busy={action.isBusy("reparse")}
                  busyLabel={t("article.attributes.reparse")}
                  onClick={() => void mutate("reparse", () => api.reparse(article.id))}
                >
                  {t("article.attributes.reparse")}
                </Button>
              ) : undefined
            }
          >
            <DataTable<ArticleAttribute>
              caption={t("article.attributes.caption")}
              columns={attributeColumns}
              rows={characteristics}
              rowKey={(row) => `${row.key}:${row.value}`}
              loadingLabel={t("article.loading")}
              empty={{
                title: t("article.attributes.empty.title"),
                description: t("article.attributes.empty.body"),
              }}
            />
          </Card>

          {mayWrite ? (
            <form
              className="hardauto-inline-form"
              onSubmit={(event) => {
                event.preventDefault();
                if (!newKey.trim()) return;
                void mutate("attr:new", async () => {
                  await api.setAttribute(article.id, newKey.trim(), { value: newValue.trim() });
                  setNewKey("");
                  setNewValue("");
                });
              }}
            >
              <div className="hardauto-inline-form__field">
                <Field label={t("article.attributes.newKey")}>
                  {(props) => (
                    <Input
                      {...props}
                      value={newKey}
                      onChange={(event) => setNewKey(event.target.value)}
                    />
                  )}
                </Field>
              </div>
              <div className="hardauto-inline-form__field">
                <Field label={t("article.attributes.newValue")}>
                  {(props) => (
                    <Input
                      {...props}
                      value={newValue}
                      onChange={(event) => setNewValue(event.target.value)}
                    />
                  )}
                </Field>
              </div>
              <div className="hardauto-inline-form__actions">
                <Button
                  type="submit"
                  variant="primary"
                  busy={action.isBusy("attr:new")}
                  busyLabel={t("article.attributes.save")}
                >
                  {t("article.attributes.add")}
                </Button>
              </div>
            </form>
          ) : null}

          <Card title={t("article.identifiers.title")} compactTitle>
            <form
              className="hardauto-inline-form"
              onSubmit={(event) => {
                event.preventDefault();
                void mutate("identifiers", () =>
                  api.updateArticle(article.id, {
                    reference_base: referenceBase.trim(),
                    code_fournisseur: codeFournisseur.trim(),
                    ipc: ipc.trim(),
                  }),
                );
              }}
            >
              <div className="hardauto-inline-form__field">
                <Field label={t("article.identifiers.referenceBase")}>
                  {(props) => (
                    <Input
                      {...props}
                      value={referenceBase}
                      disabled={!mayWrite}
                      onChange={(event) => setReferenceBase(event.target.value)}
                    />
                  )}
                </Field>
              </div>
              <div className="hardauto-inline-form__field">
                <Field label={t("article.identifiers.codeFournisseur")}>
                  {(props) => (
                    <Input
                      {...props}
                      value={codeFournisseur}
                      disabled={!mayWrite}
                      onChange={(event) => setCodeFournisseur(event.target.value)}
                    />
                  )}
                </Field>
              </div>
              <div className="hardauto-inline-form__field">
                <Field label={t("article.identifiers.ipc")}>
                  {(props) => (
                    <Input
                      {...props}
                      value={ipc}
                      disabled={!mayWrite}
                      onChange={(event) => setIpc(event.target.value)}
                    />
                  )}
                </Field>
              </div>
              {mayWrite ? (
                <div className="hardauto-inline-form__actions">
                  <Button
                    type="submit"
                    variant="primary"
                    busy={action.isBusy("identifiers")}
                    busyLabel={t("article.identifiers.save")}
                  >
                    {t("article.identifiers.save")}
                  </Button>
                </div>
              ) : null}
            </form>
          </Card>

          <Card title={t("article.equivalents.title")} compactTitle>
            {equivalents.length === 0 ? (
              <p className="hardauto-cell__muted">{t("article.equivalents.empty")}</p>
            ) : (
              <ul className="hardauto-refs">
                {equivalents.map((entry) => (
                  <li className="hardauto-refs__item" key={entry.value}>
                    {entry.value}
                  </li>
                ))}
              </ul>
            )}
          </Card>
        </div>

        {/* --- What the customer sees ------------------------------------- */}
        <div className="hardauto-article__stack">
          <Card title={t("article.visibility.title")} compactTitle>
            <Field
              label={t("article.visibility.disclosure")}
              hint={t(`article.visibility.disclosureHint.${article.stock_disclosure}`)}
            >
              {(props) => (
                <Select
                  {...props}
                  value={article.stock_disclosure}
                  disabled={!mayWrite}
                  onChange={(event) =>
                    void mutate("disclosure", () =>
                      api.updateArticle(article.id, {
                        stock_disclosure: event.target.value as StockDisclosure,
                      }),
                    )
                  }
                >
                  {DISCLOSURES.map((rule) => (
                    <option key={rule} value={rule}>
                      {t(`article.visibility.disclosureValue.${rule}`)}
                    </option>
                  ))}
                </Select>
              )}
            </Field>

            {levels.length === 0 ? (
              <p className="hardauto-cell__muted">{t("article.visibility.noStock")}</p>
            ) : (
              <div className="hardauto-levels">
                {levels.map((level) => (
                  <LevelRow key={level.site_code} level={level} locale={i18n.language} t={t} />
                ))}
              </div>
            )}
            {mayWriteStock ? (
              <p className="hardauto-article__note">{t("article.visibility.reservedHint")}</p>
            ) : null}
          </Card>

          {mayReadPrices ? (
            <ArticlePricesCard api={api} articleId={article.id} mayWrite={mayWritePrices} />
          ) : null}

          <ArticlePerimeterCard api={api} articleId={article.id} mayWrite={mayWrite} />
        </div>
      </div>
    </Page>
  );
}

/** One site's four figures. */
function LevelRow({
  level,
  locale,
  t,
}: {
  level: StockLevel;
  locale: string;
  t: (key: string) => string;
}) {
  return (
    <div className="hardauto-level">
      <div className="hardauto-level__head">
        <span>{level.site_name}</span>
        <StatusDot
          label={level.is_below_threshold ? t("stock.signal.low") : t("stock.signal.ok")}
          tone={level.is_below_threshold ? "warning" : "success"}
        />
      </div>
      <dl className="hardauto-level__figures">
        <div className="hardauto-level__figure">
          <dt>{t("article.visibility.real")}</dt>
          <dd>{quantity(level.real, locale)}</dd>
        </div>
        <div className="hardauto-level__figure">
          <dt>{t("article.visibility.reserved")}</dt>
          <dd>{quantity(level.reserved, locale)}</dd>
        </div>
        <div className="hardauto-level__figure">
          <dt>{t("article.visibility.available")}</dt>
          <dd>{quantity(level.available, locale)}</dd>
        </div>
        <div className="hardauto-level__figure">
          <dt>{t("article.visibility.forecast")}</dt>
          <dd>{quantity(level.forecast, locale)}</dd>
        </div>
      </dl>
    </div>
  );
}
